import { useState } from "react";
import { observer } from "mobx-react";
import { useStore } from "@/stores/store-context";
import { useTranslation } from "@/i18n";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { type TProfileSettingsTabKey } from "@/lib/permissions/profile-permissions";
import { ProfileGeneralPage } from "./panels/general";
import { ProfilePreferencesPage } from "./panels/preferences";
import { ProfileNotificationsPage } from "./panels/notifications";
import { ProfileSecurityPage } from "./panels/security";
import { ProfileWorkHoursPage } from "./panels/work-hours";
import { ProfileSkillsPage } from "./panels/skills";
import { ProfileConnectedAppsPage } from "./panels/connected-apps";
import { ProfileDigitalTeammatesPage } from "@/components/digital-teammates/profile-digital-teammates-page";

/** Every panel is handed `onDirtyChange`; only the explicit-submit panels (General's
 *  Save, Security's Update password) report through it, and the save-as-you-go ones
 *  ignore it. One flag serves them all because only the active tab's panel is mounted. */
const TAB_PANELS: Record<
  TProfileSettingsTabKey,
  React.ComponentType<{ onDirtyChange?: (dirty: boolean) => void }>
> = {
  general: ProfileGeneralPage,
  preferences: ProfilePreferencesPage,
  notifications: ProfileNotificationsPage,
  security: ProfileSecurityPage,
  work_hours: ProfileWorkHoursPage,
  skills: ProfileSkillsPage,
  connected_apps: ProfileConnectedAppsPage,
  digital_teammates: ProfileDigitalTeammatesPage,
};

export const ProfileSettingsModal = observer(function ProfileSettingsModal() {
  const { profileSettings } = useStore();
  const { t } = useTranslation();
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);

  return (
    <Dialog
      // Only the explicit-submit panels report into this (see TAB_PANELS). Explicit,
      // never undefined: automatic detection would also prompt after an edit on a
      // save-as-you-go tab that already saved itself (NG-2823).
      unsavedChanges={hasUnsavedEdits}
      open={profileSettings.isOpen}
      onOpenChange={(open) => profileSettings.setOpen(open)}
    >
      <DialogContent className="grid h-[min(85vh,720px)] w-full max-w-[calc(100vw-2rem)] grid-cols-1 gap-0 overflow-hidden p-0 text-sm sm:max-w-[960px] lg:grid-cols-[240px_minmax(0,1fr)]">
        <DialogTitle className="sr-only">{t("profile_settings.title")}</DialogTitle>
        <Tabs
          value={profileSettings.visibleActiveTab}
          onValueChange={(value) => profileSettings.setActiveTab(value as TProfileSettingsTabKey)}
          orientation="vertical"
          className="contents"
        >
          <aside className="overflow-y-auto border-border bg-muted/30 p-4 lg:border-r">
            <TabsList
              variant="line"
              className="h-auto w-full items-stretch gap-1 bg-transparent p-0"
            >
              {profileSettings.tabs.map((tab) => (
                <TabsTrigger
                  key={tab.key}
                  value={tab.key}
                  className="justify-start px-3 py-2 text-sm"
                >
                  {t(tab.labelKey)}
                </TabsTrigger>
              ))}
            </TabsList>
          </aside>
          <div className="min-w-0 overflow-y-auto p-6">
            {profileSettings.tabs.map((tab) => {
              const Panel = TAB_PANELS[tab.key];
              return (
                <TabsContent key={tab.key} value={tab.key}>
                  <Panel onDirtyChange={setHasUnsavedEdits} />
                </TabsContent>
              );
            })}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
});
